import { differenceInHours, format, formatDistanceToNow, isThisWeek } from 'date-fns';

/**
 * Converts a date input to a Date object
 * @param {Date | string} date - Date input
 * @returns {Date} Date object
 */
const toDate = (date: Date | string): Date => {
    return typeof date === 'string' ? new Date(date) : date;
};

/**
 * Formats a date to MM/DD/YYYY format
 * @param {Date | string} date - Date input
 * @returns {string} Formatted date string
 */
export const formatDate = (date: Date | string): string => {
    if (!date) return '';
    try {
        return format(toDate(date), 'MM/dd/yyyy');
    } catch {
        return '';
    }
};

/**
 * Formats a date to medium format with time (e.g., "Jan 15, 2024, 3:30 PM")
 * @param {Date | string} date - Date input
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (date: Date | string): string => {
    if (!date) return '';
    try {
        const dateObj = toDate(date);
        return new Intl.DateTimeFormat('en-US', {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(dateObj);
    } catch {
        return '';
    }
};

/**
 * Formats a date to medium format (e.g., "Jan 15, 2024")
 * @param {Date | string} date - Date input
 * @returns {string} Formatted date string
 */
export const formatDateMedium = (date: Date | string): string => {
    if (!date) return '';
    try {
        const dateObj = toDate(date);
        return new Intl.DateTimeFormat('en-US', {
            dateStyle: 'medium',
        }).format(dateObj);
    } catch {
        return '';
    }
};

/**
 * Formats a date to long format (e.g., "January 15, 2024")
 * @param {Date | string} date - Date input
 * @returns {string} Formatted date string
 */
export const formatDateLong = (date: Date | string): string => {
    if (!date) return '';
    try {
        return format(toDate(date), 'MMMM d, yyyy');
    } catch {
        return '';
    }
};

/**
 * Formats a date relative to now
 * - < 24 hours: shows time only (e.g., "3:30 PM")
 * - < 7 days: shows weekday (e.g., "Mon")
 * - >= 7 days: shows short date (e.g., "Jan 15")
 * @param {Date | string} date - Date input
 * @returns {string} Formatted relative date string
 */
export const formatRelativeDate = (date: Date | string): string => {
    if (!date) return '';
    try {
        const dateObj = toDate(date);
        const now = new Date();
        const hoursDiff = differenceInHours(now, dateObj);

        if (hoursDiff < 24) {
            return format(dateObj, 'h:mm a');
        } else if (isThisWeek(dateObj, { weekStartsOn: 0 })) {
            return format(dateObj, 'EEE');
        } else {
            return format(dateObj, 'MMM d');
        }
    } catch {
        return '';
    }
};

/**
 * Formats a date as "time ago" (e.g., "3 hours ago", "2 days ago")
 * @param {Date | string} date - Date input
 * @returns {string} Formatted distance string
 */
export const formatTimeAgo = (date: Date | string): string => {
    if (!date) return '';
    try {
        return formatDistanceToNow(toDate(date), { addSuffix: true });
    } catch {
        return '';
    }
};

/**
 * Generates initials from a name string
 * @param {string} name - Full name
 * @returns {string} Initials or special case for MFour
 */
export const getInitials = (name: string): string => {
    if (['MFour', 'MFour Studio', 'MFour Data Research'].includes(name)) {
        return 'M4';
    }
    return name
        .split(' ')
        .map((word) => word[0])
        .join('')
        .toUpperCase();
};
